import { AppIcon } from '@/components/ui/app-icon';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSubmitReviewCreditMutation } from '@/store/features/review-credits.feature';
import { useState } from 'react';
import { toast } from 'sonner';
import { iconSpinnerThird, iconStar } from '@/lib/icons';

/** The reward amount, kept in sync with REVIEW_REWARD_CREDITS on the backend
 *  (cleanse_api/core/config.py). Display-only. */
export const REVIEW_REWARD_CREDITS = 1000;

/** Loose client-side check mirroring is_valid_appsumo_review_url on the BE.
 *  The server is authoritative — this only gives instant feedback. */
function looksLikeAppsumoReviewUrl(url: string): boolean {
  try {
    const u = new URL(url.trim());
    const host = u.hostname.toLowerCase();
    const isAppsumo = host === 'appsumo.com' || host === 'www.appsumo.com';
    return (
      (u.protocol === 'https:' || u.protocol === 'http:') &&
      isAppsumo &&
      u.pathname.toLowerCase().includes('/reviews/')
    );
  } catch {
    return false;
  }
}

interface ReviewRewardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ReviewRewardDialog({
  open,
  onOpenChange,
}: ReviewRewardDialogProps) {
  const [url, setUrl] = useState('');
  const [submit, { isLoading }] = useSubmitReviewCreditMutation();

  const trimmed = url.trim();
  const valid = looksLikeAppsumoReviewUrl(trimmed);

  const handleSubmit = async () => {
    if (!valid) {
      toast.error('Enter a valid AppSumo review link');
      return;
    }
    try {
      await submit({ review_url: trimmed }).unwrap();
      toast.success(
        'Review submitted! Our team will approve it and add your credits shortly.'
      );
      setUrl('');
      onOpenChange(false);
    } catch (err) {
      const detail = (err as { data?: { detail?: string } })?.data?.detail;
      toast.error(
        typeof detail === 'string' ? detail : 'Could not submit your review'
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <AppIcon
              icon={iconStar}
              className='text-amber-500'
              style={{ fontSize: 16 }}
            />
            Get {REVIEW_REWARD_CREDITS.toLocaleString()} credits for a review
          </DialogTitle>
          <DialogDescription>
            Left us a review on AppSumo? Paste the link below. Once our team
            approves it, {REVIEW_REWARD_CREDITS.toLocaleString()} credits are
            added to your workspace — one reward per workspace.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-2 py-1'>
          <Label htmlFor='review-url' className='text-xs text-muted-foreground'>
            AppSumo review URL
          </Label>
          <Input
            id='review-url'
            type='url'
            inputMode='url'
            autoFocus
            placeholder='https://appsumo.com/products/cleanlist-ai/reviews/…'
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && valid && !isLoading) handleSubmit();
            }}
          />
          {trimmed.length > 0 && !valid && (
            <p className='text-[11px] text-amber-600 dark:text-amber-400'>
              That doesn't look like an AppSumo review link. It should look like
              https://appsumo.com/products/cleanlist-ai/reviews/…
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!valid || isLoading}>
            {isLoading ? (
              <>
                <AppIcon
                  icon={iconSpinnerThird}
                  className='mr-1.5 animate-spin'
                  style={{ fontSize: 14 }}
                />
                Submitting…
              </>
            ) : (
              'Submit for review'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
